import { useState } from "react";
import Layout from "@/components/feature/Layout";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { brandInfo, teamMembers, testimonials } from "@/mocks/brand";

const CONTACT_FORM_ADDR = "https://readdy.ai/api/form/d9ri09ee3oq4jqumu1kg";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `关于我们 - ${brandInfo.name}`,
  description: brandInfo.description,
  url: "https://lolitastyle.cn/about",
};

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      setFormMessage("消息已发送，我们会尽快回复你！");
      form.reset();
      return;
    }

    setFormStatus("loading");
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "phone_alt") {
          params.append(key, value as string);
        }
      });
      const response = await fetch(CONTACT_FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && (parsed as { code?: string }).code === "OK") {
        setFormStatus("success");
        setFormMessage("感谢你的留言！我们会尽快回复~");
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || responseText;
        setFormStatus("error");
        setFormMessage(String(serverMsg || "发送失败，请稍后重试"));
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络错误，请稍后重试");
    } finally {
      setTimeout(() => {
        setFormStatus("idle");
        setFormMessage("");
      }, 5000);
    }
  };

  const stars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i key={i} className={`text-xs ${i < rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-300"}`}></i>
    ));
  };

  return (
    <Layout>
      <SEOHead
        title={`关于我们 - ${brandInfo.name} | 品牌介绍与联系方式`}
        description={`了解${brandInfo.name}的品牌故事、团队成员与口碑评价。${brandInfo.description}`}
        keywords="关于我们,萝莉风格,品牌介绍,联系我们,萝莉穿搭团队"
        canonicalUrl="https://lolitastyle.cn/about"
        ogImage="https://readdy.ai/api/search-image?query=Cozy%20fashion%20studio%20interior%20with%20pastel%20decor%2C%20lolita%20dress%20display%2C%20creative%20workspace%2C%20warm%20atmosphere%2C%20editorial%20photography&width=1200&height=630&seq=og-about&orientation=landscape"
      />

      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <div className="pt-16">
        <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              关于<span className="text-primary-500">我们</span>
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {brandInfo.description}
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              品牌<span className="text-primary-500">故事</span>
            </h2>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="w-full lg:w-1/2">
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  <strong>{brandInfo.name}</strong>创立于{brandInfo.founded}，起源于创始人对洛丽塔时尚文化的深深热爱。我们注意到，虽然洛丽塔风格在中国年轻女性中越来越受欢迎，但系统化、高质量的中文穿搭指南仍然十分匮乏。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  于是，一群志同道合的洛丽塔爱好者聚集在一起，打造了这个专注于<strong>萝莉风格时尚穿搭</strong>的内容平台。我们希望用专业的知识和真诚的分享，帮助每一位对甜美穿搭感兴趣的女孩找到属于自己的风格。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  从最初的个人博客发展到今天，我们已累计发布数十篇原创穿搭指南，覆盖洛丽塔文化科普、甜美穿搭技巧、配件搭配推荐等多个领域，服务了数以万计的穿搭爱好者。
                </p>
              </div>
              <div className="w-full lg:w-1/2 relative h-56 md:h-72 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20creative%20fashion%20studio%20workspace%20with%20pastel%20pink%20decor%2C%20lolita%20dress%20sketches%20on%20table%2C%20fabric%20swatches%2C%20inspiration%20board%20with%20fashion%20photos%2C%20warm%20natural%20light%2C%20editorial%20interior%20photography&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="品牌故事"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              我们的<span className="text-primary-500">团队</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-background-50 rounded-lg p-6 text-center">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-4 bg-background-200">
                    <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-1">{member.name}</h3>
                  <span className="inline-block px-3 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
                    {member.role}
                  </span>
                  <p className="text-sm text-foreground-500 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-2" style={{ fontFamily: "var(--font-heading)" }}>
              读者<span className="text-primary-500">口碑</span>
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">听听我们的读者怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-2">{stars(t.rating)}</div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{t.comment}&rdquo;</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground-950">{t.name}</span>
                    <span className="text-xs text-foreground-400">· {t.location}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              <span className="text-primary-500">联系</span>我们
            </h2>
            <div className="bg-background-50 rounded-lg p-6 md:p-8">
              <form onSubmit={handleContactSubmit} data-readdy-form="" id="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full h-10 px-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full h-10 px-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full h-10 px-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="你想聊什么？"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你的留言（最多500字）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="h-0 w-0 absolute opacity-0 -z-10"
                />
                <button
                  type="submit"
                  disabled={formStatus === "loading"}
                  className="w-full h-11 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {formStatus === "loading" ? "发送中..." : "发送消息"}
                </button>
                {formMessage && (
                  <p className={`mt-3 text-sm text-center ${
                    formStatus === "success" ? "text-accent-600" : "text-foreground-600"
                  }`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-lg bg-background-50">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </div>
                <p className="text-xs font-medium text-foreground-950 mb-1">邮箱</p>
                <p className="text-xs text-foreground-500">{brandInfo.email}</p>
              </div>
              <div className="p-4 rounded-lg bg-background-50">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-map-pin-line text-accent-500 text-lg"></i>
                </div>
                <p className="text-xs font-medium text-foreground-950 mb-1">地址</p>
                <p className="text-xs text-foreground-500">{brandInfo.location}</p>
              </div>
              <div className="p-4 rounded-lg bg-background-50">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-phone-line text-secondary-500 text-lg"></i>
                </div>
                <p className="text-xs font-medium text-foreground-950 mb-1">电话</p>
                <p className="text-xs text-foreground-500">{brandInfo.phone}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}